import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import heroBgImage from '../assets/hero-bg.png';
/**
 * Hero component with elegant artistic gradient background and modern typography.
 * Features clean design with compelling messaging and clear call-to-actions.
 */
const Hero = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const painPoints = [
    "Using Excel Spreadsheets as Your CRM?",
    "Per-User CRM Fees That Punish You for Growing?",
    "Paying for 100 CRM Features But Only Using 5?",
    "A CRM That Lives in Its Own Island?",
    "Bending Your Business to Fit Your CRM's Limitations?",
    "A CRM So Complex Your Team Avoids Using It?",
    "A CRM That Creates More Work Instead of Saving Time?"
  ];

  // Typewriter effect with pause
  useEffect(() => {
    const currentFullText = painPoints[currentIndex];

    if (isPaused) {
      const pauseTimer = setTimeout(() => setIsPaused(false), 2000);
      return () => clearTimeout(pauseTimer);
    }

    const typingSpeed = isDeleting ? 30 : 80;

    const timer = setTimeout(() => {
      if (!isDeleting) {
        // Typing
        if (displayText.length < currentFullText.length) {
          setDisplayText(currentFullText.slice(0, displayText.length + 1));
        } else {
          setIsPaused(true);
          setIsDeleting(true);
        }
      } else {
        // Deleting
        if (displayText.length > 0) {
          setDisplayText(displayText.slice(0, -1));
        } else {
          setIsDeleting(false);
          setCurrentIndex((prev) => (prev + 1) % painPoints.length);
        }
      }
    }, typingSpeed);

    return () => clearTimeout(timer);
  }, [displayText, isDeleting, isPaused, currentIndex, painPoints]);

  const loomEmbedUrl = 'https://www.loom.com/embed/f5fb7c5273814a5c93590e98abbc3565?autoplay=1&mute=1&hide_owner=true&hide_share=true&hide_title=true';

  return (
    <section className="hero" id="hero">
      {/* Premium faded background image */}
      <div 
        className="hero-image-bg" 
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${heroBgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.6,
          zIndex: 0,
          pointerEvents: 'none'
        }}
        aria-hidden="true"
      />
      <div className="hero-content">
        <motion.div 
          className="hero-layout"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="hero-main">
            <h1 className="hero-title">
              Build a CRM that fits your process<br className="hero-title-break" /> and drives measurable growth.
            </h1>
            
            <p className="hero-problem-lead">Are you frustrated with:</p>
            
            <div className="hero-typewriter-wrap">
              <span className="hero-typewriter-text">
                {displayText}
                <span className="text-primary" style={{ animation: 'blink 1s step-start infinite', fontWeight: 'bold' }}>|</span>
              </span>
            </div>
            
            <motion.div 
              className="hero-cta-row"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              <a href="https://calendly.com/aakeeo-technologies/crm-consultations" target="_blank" rel="noopener noreferrer" className="btn-primary">
                Book CRM Strategy Call
              </a>
              <Link to="/services" className="btn-secondary">
                Explore Our Solutions
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </div>
      
      <div 
        className="hero-scroll-indicator"
        onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
        title="Scroll down"
      >
        <i className="fa-solid fa-chevron-down"></i>
      </div>
    </section>
  );
};


export default Hero;
